import { getHubConfig } from 'const/HubConfig';
import { Map, Marker } from 'mapbox-gl';
import React, { useEffect, useRef } from 'react';
import { renderToString } from 'react-dom/server';
import { MdEmojiTransportation } from 'react-icons/md';
import { Region } from 'types/orderTypes';
import './Pinpoint.scss';

interface PinpointProps {
  map: Map;
  region: Region;
  data?: {
    lon: number; // Longitude
    lat: number; // Latitude
  };
  draggable?: boolean;
  isUsingHubAsDefault?: boolean;
  onDragEnd?: (newData: { lon: number; lat: number }) => void;
}

export const Pinpoint = ({ map, onDragEnd, ...props }: PinpointProps) => {
  const marker = useRef<Marker>();

  const markerCleanup = () => {
    marker.current && marker.current.remove();
  };

  useEffect(() => {
    const lon = props.data?.lon;
    const lat = props.data?.lat;

    const isDraggable = props.draggable;

    /* Create a DOM element for Pinpoint marker */
    if (lon && lat) {
      /* The container element */
      const pinpointMarkerElement = document.createElement('div');
      pinpointMarkerElement.className = `pinpoint-marker`;

      /* The pointer element */
      const pointerElement = document.createElement('div');
      pointerElement.className = `pointer ${isDraggable && 'draggable'}`;
      pinpointMarkerElement.append(pointerElement);

      /* The icon element */
      const iconElement = document.createElement('div');
      iconElement.className = `icon ${isDraggable && 'draggable'}`;
      iconElement.innerHTML = renderToString(
        <MdEmojiTransportation size={28} />
      );
      pinpointMarkerElement.append(iconElement);

      /* The dot element */
      const dotElement = document.createElement('div');
      dotElement.className = `dot`;
      dotElement.innerHTML = '•';
      pinpointMarkerElement.append(dotElement);

      marker.current = new Marker({
        draggable: isDraggable,
        element: pinpointMarkerElement,
      })
        .setLngLat([lon, lat])
        .addTo(map);
    } else {
      markerCleanup();
    }

    /* Dragging action */
    const onDragEndAction = () => {
      const lngLat = marker.current.getLngLat();
      onDragEnd({ lon: lngLat.lng, lat: lngLat.lat });
    };
    marker.current && marker.current.on('dragend', onDragEndAction);

    return () => {
      marker.current && marker.current.off('dragend', onDragEndAction);

      /* Cleanup the Pinpoint marker */
      markerCleanup();
    };
  }, [map, onDragEnd, props.data, props.draggable]);

  /* Set the HUB location as default for empty marker */
  useEffect(() => {
    const lon = props.data?.lon;
    const lat = props.data?.lat;
    const isEmptyDefaultValue = !lon && !lat;

    const hubPosition = getHubConfig(props.region)?.hubAddress?.location;

    /* The default HUB location only available when the Pinpoint is using the Hub as Default Value */
    if (isEmptyDefaultValue && props.isUsingHubAsDefault && hubPosition) {
      onDragEnd({
        lon: hubPosition.longitude,
        lat: hubPosition.latitude,
      });
    }
  }, [
    onDragEnd,
    props.data?.lat,
    props.data?.lon,
    props.isUsingHubAsDefault,
    props.region,
  ]);

  return <></>;
};
